import {
  BooleanField,
  DependencyFunction,
  IModelContext,
  ModelDependency,
  ModelGroup,
  ModelItem,
  ModelItemOptions,
  NumberField,
  ReadOnlyFunction,
  requiredValueOptionsValidation,
  StringField,
  ValueOptions
} from '@mng/model';
import { AbschlussGrund, MittelHerkunft, Person } from '../../antrag.model.types';
import {
  abschlussGrundValidation,
  geldwaescheValidation,
  isAbschlussGrundBlockRequired,
  mittelHerkunftValidation
} from './geldwaesche.validation';

// -----------
// GELDWÄSCHE

export class GeldwaescheModelDef {
  static createGeldwaescheModel(): ModelGroup {
    const geldwaescheModel: ModelGroup = new ModelGroup('geldwaesche', 'Geldwäsche')
      .addGroupObject(this.createMittelHerkunftModel())
      .addGroupObject(this.createAbschlussGrundModel().withFunctionDependency(isAbschlussGrundBlockRequired))
      .addGroupObject(this.createEinkommenModel());

    geldwaescheModel.options = new ModelItemOptions([geldwaescheValidation]);

    return geldwaescheModel;
  }

  static createMittelHerkunftModel(): ModelGroup {
    const mittelHerkunftGrundSonstiges: BooleanField = new BooleanField('sonstiges', 'Sonstiges');
    const mittelHerkunftGrundSonstigesDesc: StringField = new StringField('sonstigesDesc', 'Beschreibung')
      .withRequired()
      .withFunctionDependency(isMittelHerkunftRequired);

    const mittelHerkunftModel: ModelGroup = new ModelGroup('mittelHerkunft', 'Mittel Herkunft').addItems(
      new BooleanField('einkommenAusBeruflicherTaetigkeit', 'Einkommen aus beruflicher Tätigkeit'),
      new BooleanField('erbschaft', 'Erbschaft'),
      new BooleanField('ersparnisse', 'Ersparnisse/Auszahlung einer Lebensversicherung'),
      new BooleanField('verkauf', 'Verkauf (z.b. Immobilien, Aktien, Wertpapiere)'),
      mittelHerkunftGrundSonstiges,
      mittelHerkunftGrundSonstigesDesc
    );

    mittelHerkunftModel.options = new ModelItemOptions([mittelHerkunftValidation]);

    return mittelHerkunftModel;
  }

  static createAbschlussGrundModel(): ModelGroup {
    const abschlussGrundSonstiges: BooleanField = new BooleanField('sonstiges', 'Sonstiges');
    const abschlussGrundSonstigesDesc: StringField = new StringField('sonstigesDesc', 'Beschreibung')
      .withRequired()
      .withFunctionDependency(isAbschlussGrundDescRequired);

    const abschlussGrundModel: ModelGroup = new ModelGroup(
      'abschlussGrund',
      'Grund dieses Versicherungsabschlusses'
    ).addItems(
      new BooleanField('absicherungFamilie', 'Absicherung der Familie'),
      new BooleanField('vermoegensaufbau', 'Vermögensaufbau'),
      new BooleanField('ausbildungKinder', 'Ausbildung der Kinder'),
      new BooleanField('pensionsVorsorge', 'Pensionsvorsorge'),
      new BooleanField('absicherungDarlehen', 'Absicherung Darlehen'),
      abschlussGrundSonstiges,
      abschlussGrundSonstigesDesc
    );
    abschlussGrundModel.options = new ModelItemOptions([abschlussGrundValidation]);

    return abschlussGrundModel;
  }

  static createEinkommenModel(): ModelGroup {
    return new ModelGroup('einkommen', 'Angaben zum durchschnittlichen monatlichen Bruttoeinkommen').addItems(
      new NumberField('einkommenHoehe', '')
        .withOptions(new ModelItemOptions([requiredValueOptionsValidation]).withValueOptions(einkommenValueOptions))
        .withReadOnlyFunction(isReadOnlyEinkommen)
    );
  }
}

const isReadOnlyEinkommen: ReadOnlyFunction = (modelItem: ModelItem, modelContext: IModelContext): boolean => {
  const person: Person = modelContext.dataStack[modelContext.dataStack.length - 3];
  return (
    person &&
    person.bruttoeinkommenrange &&
    person.bruttoeinkommenrange > 0 &&
    person.personRollen &&
    person.personRollen.vn
  );
};

export const isMittelHerkunftRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const data: MittelHerkunft = modelContext.dataStack[modelContext.dataStack.length - 1];
  return data.sonstiges;
};

export const isAbschlussGrundDescRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const data: AbschlussGrund = modelContext.dataStack[modelContext.dataStack.length - 1];
  return data.sonstiges;
};

export const einkommenValueOptions: ValueOptions = new ValueOptions([
  { name: 'unter EUR 1.500,--', value: 1 },
  {
    name: 'EUR 1.500,-- bis EUR 3.600,--',
    value: 2
  },
  { name: 'über EUR 3.600,--', value: 3 }
]);
